import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import projects from "../data/projects";
import Media from "../components/Media";
import Gallery from "../components/Gallery";
import Lightbox from "../components/Lightbox";
import NotFoundPage from "./NotFoundPage";
import useDocumentTitle from "../hooks/useDocumentTitle";

/**
 * A single project, built entirely from its entry in `src/data/projects.js`.
 * The URL (/cadavre-exquis…) is matched against each project's `slug`.
 *
 * Page structure, top to bottom:
 *   1. Back link      ← Index
 *   2. Title
 *   3. Intro paragraph
 *   4. Hero           the index cover, unless a section already shows it
 *   5. Sections       text (sticky, left) beside media (right)
 *   6. Gallery        optional rows of extra media
 *
 * Images and videos in the sections and gallery open fullscreen in the
 * <Lightbox /> when clicked (Vimeo embeds have their own fullscreen button).
 */
export default function ProjectPage() {
  const { slug } = useParams();
  const project = projects.find((p) => p.slug === slug);

  // Which picture is open fullscreen (an index into `viewable`), or null
  const [viewing, setViewing] = useState(null);

  useDocumentTitle(project?.title);

  if (!project) return <NotFoundPage />;

  const { title, intro, cover, sections = [], gallery = [] } = project;

  // Avoid showing the same picture twice in a row.
  const coverIsInSections = sections.some((s) => s.media?.src === cover.src);

  // Everything that can be viewed fullscreen, in page order. The viewer's
  // ← / → step through this list.
  const sectionMedia = sections.map((s) => s.media).filter(Boolean);
  const viewable = [...sectionMedia, ...gallery].filter(
    (item) => item.type !== "vimeo"
  );
  const open = (item) => setViewing(viewable.indexOf(item));

  return (
    <article className="project">
      {/* 1. Back link */}
      <div className="project__meta mono">
        <Link to="/" className="back-link">
          ← Portfolio
        </Link>
      </div>

      {/* 2. Title */}
      <h1 className="project__title">{title}</h1>

      {/* 3. Intro (in the right-hand column, lined up with the media below) */}
      <div className="project__intro-row">
        <p className="project__intro">{intro}</p>
      </div>

      {/* 4. Hero */}
      {!coverIsInSections && (
        <figure className="project__hero">
          <Media item={cover} eager />
        </figure>
      )}

      {/* 5. Sections */}
      {sections.map((section, i) => (
        <section className="project__section" key={i}>
          {section.text && (
            <div className="project__text">
              {/* Small running counter, e.g. 01 / 03 */}
              <span className="project__step mono">
                {String(i + 1).padStart(2, "0")} /{" "}
                {String(sections.length).padStart(2, "0")}
              </span>
              {section.text.map((paragraph, j) => (
                <p key={j}>{paragraph}</p>
              ))}
            </div>
          )}
          {section.media && (
            <figure className="project__figure">
              {section.media.type === "vimeo" ? (
                <Media item={section.media} />
              ) : (
                <button
                  type="button"
                  className="zoomable"
                  onClick={() => open(section.media)}
                  aria-label={`View ${section.media.alt || "image"} fullscreen`}
                >
                  <Media item={section.media} />
                </button>
              )}
            </figure>
          )}
        </section>
      ))}

      {/* 6. Gallery */}
      {gallery.length > 0 && (
        <div className="project__gallery">
          <Gallery items={gallery} onOpen={(i) => open(gallery[i])} />
        </div>
      )}

      {/* Fullscreen viewer */}
      {viewing !== null && viewing >= 0 && (
        <Lightbox
          items={viewable}
          index={viewing}
          onChange={setViewing}
          onClose={() => setViewing(null)}
        />
      )}
    </article>
  );
}
